import { Play } from 'lucide-react';

import { fmt, fmtDuration, fmtRelative } from '../utils/format';

// Segmented horizontal progress bar for a single running batch.
// Three segments: succeeded (green), failed (red), remaining (light gray stripe).
export const BatchProgressCard = ({ batch }) => {
  const total = batch.total ?? 0;
  const processed = batch.processed ?? 0;
  const failed = batch.failed ?? 0;
  const succeeded = Math.max(0, processed - failed);
  const remaining = Math.max(0, total - processed);

  const denom = Math.max(1, total);
  const pctSucceeded = (succeeded / denom) * 100;
  const pctFailed = (failed / denom) * 100;
  const pctRemaining = (remaining / denom) * 100;

  const percentDone = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 bg-white hover:shadow-sm transition-all duration-200">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex items-center justify-center w-6 h-6 rounded bg-darkBlueColor/10 text-darkBlueColor flex-shrink-0">
            <Play size={12} fill="currentColor" />
          </span>
          <span className="text-sm font-semibold text-blackColor">
            Batch #{batch.id}
          </span>
          <span className="text-[11px] text-darkGrayColor truncate">
            {batch.user_email ? `by ${batch.user_email}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-darkGrayColor">
          <span>Started {fmtRelative(batch.started_at)} ago</span>
          <span className="text-blackColor font-medium">
            ETA {fmtDuration(batch.eta_seconds)}
          </span>
        </div>
      </div>

      <div className="flex h-2 w-full overflow-hidden rounded bg-lightGrayColor">
        {pctSucceeded > 0 && (
          <div
            className="bg-greenColor transition-all duration-200"
            style={{ width: `${pctSucceeded}%` }}
            title={`Succeeded: ${succeeded}`}
          />
        )}
        {pctFailed > 0 && (
          <div
            className="bg-redColor transition-all duration-200"
            style={{ width: `${pctFailed}%` }}
            title={`Failed: ${failed}`}
          />
        )}
        {pctRemaining > 0 && (
          <div
            className="transition-all duration-200"
            style={{
              width: `${pctRemaining}%`,
              backgroundImage:
                'repeating-linear-gradient(45deg, #f5f5f5 0 6px, #fcfcfc 6px 12px)',
            }}
            title={`Remaining: ${remaining}`}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-darkGrayColor">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-blackColor font-medium">
            {fmt(processed)} of {fmt(total)}
          </span>
          <span className="text-purpleColor font-medium">{percentDone}%</span>
          <span>{fmt(batch.throughput_per_min)} /min</span>
          <span className={failed > 0 ? 'text-redColor font-medium' : ''}>
            {fmt(failed)} failed
          </span>
        </div>
        <span className="text-[11px]">status: {batch.status}</span>
      </div>
    </div>
  );
};
